// @flow

import * as React from 'react';

import CalculationBasicInfoInput from './CalculationBasicInfoInput';
import CalculationTraditionalDiggingInput from './CalculationTraditionalDiggingInput';

import OptionManager from '../common/OptionManager';
import DependencyManager from '../common/DependencyManager';
import CalculationInsertionFlexibleLiningInput from "./CalculationInsertionFlexibleLiningInput";
import CalculationTubeCrackingInput from "./CalculationTubeCrackingInput";
import ValidationManager from "../common/ValidationManager";
import CalculationBasicInfoAfterInput from "./CalculationBasicInfoAfterInput";
import CalculationTechInput from "./CalculationTechInput";
import DefaultValueManager from "../common/DefaultValueManager";
import CalculationDistancesInput from "./CalculationDistancesInput";
import type {Action} from "../store/Calculation";
import type {DependencyContext} from "../common/DependencyManager";
import CalculationDirectedDrillingInput from "./CalculationDirectedDrillingInput";
import CalculationSlipliningInput from "./CalculationSlipliningInput";
import CalculationPuLinerInput from "./CalculationPuLinerInput";
import ReactAI from 'react-appinsights';


export type PhaseState = {
    touched: {[name:string]: boolean},
    error: {[name:string]: ?string},

    // Basic info
    techniquesToCompare: number,
    typeOfLandscapeBefore: number,
    typeOfLandscapeAfter: number,
    coatingBefore: number,
    coatingAfter: number,
    asphaltTypeAfter: number,
    streetTypeBefore: number,
    streetTypeAfter: number,

    numExistingTubes: number,
    existingTube1TubeType: number,
    existingTube2TubeType: number,
    existingTube3TubeType: number,
    existingTube1TubeMaterial: number,
    existingTube2TubeMaterial: number,
    existingTube3TubeMaterial: number,
    existingTube1TubeDiameter: number,
    existingTube2TubeDiameter: number,
    existingTube3TubeDiameter: number,
    existingTube1TubeLength: number,
    existingTube2TubeLength: number,
    existingTube3TubeLength: number,

    // Distances
    distanceReceiverFillingKm: number,
    distanceReceiverCleanAsphaltKm: number,
    distanceReceiverPollutedAsphaltKm: number,
    distanceFillingKm: number,
    distanceReceiverOldTubesKm: number,

    // Traditional digging
    shaftDepthM: number,
    slopeAngleN1: number,
    shaftCapacityM3PerHour: number,
    shaftLengthM: number,

    numTraditionalTubes: number,
    traditionalTube1TubeType: number,
    traditionalTube2TubeType: number,
    traditionalTube3TubeType: number,
    traditionalTube1TubeMaterial: number,
    traditionalTube2TubeMaterial: number,
    traditionalTube3TubeMaterial: number,
    traditionalTube1TubeDiameter: number,
    traditionalTube2TubeDiameter: number,
    traditionalTube3TubeDiameter: number,
    traditionalTube1TubeLength: number,
    traditionalTube2TubeLength: number,
    traditionalTube3TubeLength: number,

    // Insertion flexible lining
    iflPointShafts: number,
    iflMetersLongerBottomLengthThanWidth: number,
    iflShaftDepthM: number,
    iflSlopeAngleN1: number,
    iflShaftCapacityM3PerHour: number,
    iflShaftLengthM: number,

    numIflTubes: number,
    iflTube1TubeType: number,
    iflTube2TubeType: number,
    iflTube1TubeDiameter: number,
    iflTube2TubeDiameter: number,
    iflTube1TubeLength: number,
    iflTube2TubeLength: number,

    // Tube cracking
    tcPointShafts: number,
    tcMetersLongerBottomLengthThanWidth: number,
    tcShaftDepthM: number,
    tcSlopeAngleN1: number,
    tcShaftCapacityM3PerHour: number,
    tcShaftLengthM: number,

    numTcTubes: number,
    tcTube1TubeType: number,
    tcTube2TubeType: number,
    tcTube3TubeType: number,
    tcTube1TubeMaterial: number,
    tcTube2TubeMaterial: number,
    tcTube3TubeMaterial: number,
    tcTube1TubeDiameter: number,
    tcTube2TubeDiameter: number,
    tcTube3TubeDiameter: number,
    tcTube1TubeLength: number,
    tcTube2TubeLength: number,
    tcTube3TubeLength: number,

    // Directed drilling
    ddPointShafts: number,
    ddMetersLongerBottomLengthThanWidth: number,
    ddShaftDepthM: number,
    ddSlopeAngleN1: number,
    ddShaftCapacityM3PerHour: number,
    ddShaftLengthM: number,

    numDdTubes: number,
    ddTube1TubeType: number,
    ddTube2TubeType: number,
    ddTube3TubeType: number,
    ddTube1TubeMaterial: number,
    ddTube2TubeMaterial: number,
    ddTube3TubeMaterial: number,
    ddTube1TubeDiameter: number,
    ddTube2TubeDiameter: number,
    ddTube3TubeDiameter: number,
    ddTube1TubeLength: number,
    ddTube2TubeLength: number,
    ddTube3TubeLength: number,
    
    // Sliplining
    slPointShafts: number,
    slMetersLongerBottomLengthThanWidth: number,
    slShaftDepthM: number,
    slSlopeAngleN1: number,
    slShaftCapacityM3PerHour: number,
    slShaftLengthM: number,

    numSlTubes: number,
    slTube1TubeType: number,
    slTube1TubeMaterial: number,
    slTube1TubeDiameter: number,
    slTube1TubeLength: number,

    // PU lining
    pulPointShafts: number,
    pulMetersLongerBottomLengthThanWidth: number,
    pulShaftDepthM: number,
    pulSlopeAngleN1: number,
    pulShaftCapacityM3PerHour: number,
    pulShaftLengthM: number,

    numPulTubes: number,
    pulTube1TubeType: number,
    pulTube1TubeDiameter: number,
    pulTube1TubeLength: number,

}

type Props = {

    optionManager: OptionManager,
    defaultValueManager: DefaultValueManager,
    dependencyManager: DependencyManager,
    validationManager: ValidationManager,

    phaseNr: number,
    phaseState: PhaseState,
    totalPhaseCount: number,
    
    clearCalculationResult: () => Action,
    changePhaseValues: (phaseNr: number, partialPhaseState: Object) => Action,
    removePhase: (phaseNr: number) => void

}

class CalculationPhaseInput extends React.Component<Props> {
    
    unmounting: boolean;
    
    constructor(props:Props) {
        super(props);
        this.unmounting = false;
    }
    
    componentDidMount() {
    }

    componentWillUnmount() {
        this.unmounting = true;
    }

    handleChange = (event: SyntheticEvent<>): void => {
        let name = this.getNameFromEvent(event);
        let value = this.getValueFromEvent(event);

        // Update dependent values (hidden and limited choices for example)
        let newPartialState = this.props.dependencyManager.handleDependencies(name, value, this.createDependencyContext(this.props.phaseState));
        
        this.props.changePhaseValues(this.props.phaseNr, newPartialState);
        
        // If already touched (in error for example, revalidate)
        // TODO: Should probably be done for all dependent values in newPartialState
        if(this.props.phaseState.touched[name])
            this.validate(name, value);
        
        this.props.clearCalculationResult();
    };

    handleBlur = (event: SyntheticEvent<>): void => {
        let name = this.getNameFromEvent(event);
        let value = this.getValueFromEvent(event);

        // Update touched status
        let newPartialState = {
            touched: {
                [name]: true
            }
        };
        this.props.changePhaseValues(this.props.phaseNr, newPartialState);
        
        // Validate
        this.validate(name, value);
    };

    handleUnmount = (htmlName: string): void => {
        // Reset touched and error meta data for the unmounted component
        if(!this.unmounting) {
            let newPartialState = {
                touched: {
                    [htmlName]: false
                },
                error: {
                    [htmlName]: undefined
                },
            };

            this.props.changePhaseValues(this.props.phaseNr, newPartialState);
        }
    };

    getNameFromEvent(event: SyntheticEvent<>): string {
        const target = event.currentTarget;
        if(target instanceof HTMLInputElement) {
            return target.name;
        }
        else if(target instanceof HTMLSelectElement) {
            return target.name;
        }
        else {
            throw new Error(`Can't get name from target of type ${typeof target}`);
        }
    }
    
    getValueFromEvent(event: SyntheticEvent<>): number {
        const target = event.currentTarget;
        if(target instanceof HTMLInputElement) {
            let num = Number(target.value);
            if(isNaN(num))
                num = -1;
            if(target.value === "")
                num = -1;
            return num;
        }
        else if(target instanceof HTMLSelectElement) {
            return parseInt(target.value, 10);
        }
        else {
            throw new Error(`Can't get name from target of type ${typeof target}`);
        }
    }
    
    validate(name: string, value: number) {
        let validationResult = this.props.validationManager.validate(name, value);
        let newPartialErrorState = this.props.validationManager.createPartialErrorObj(name, validationResult);
        this.props.changePhaseValues(this.props.phaseNr, {error: newPartialErrorState});
    }
    
    render = () => {
        const existingTubesNamePrefix = "existingTube";
        const traditionalTubesNamePrefix = "traditionalTube";
        const iflTubesNamePrefix = "iflTube";
        const tcTubesNamePrefix = "tcTube";
        const ddTubesNamePrefix = "ddTube";
        const slTubesNamePrefix = "slTube";
        const pulTubesNamePrefix = "pulTube";

        const techChosen = this.props.phaseState.techniquesToCompare !== -1;
        const showIfl = this.props.dependencyManager.isTechnologyValid(
            this.props.optionManager.availableSelections.techniquesToCompare, "_IFL", this.props.phaseState.techniquesToCompare);
        const showTc = this.props.dependencyManager.isTechnologyValid(
            this.props.optionManager.availableSelections.techniquesToCompare, "_TC", this.props.phaseState.techniquesToCompare);
        const showDd = this.props.dependencyManager.isTechnologyValid(
            this.props.optionManager.availableSelections.techniquesToCompare, "_DD", this.props.phaseState.techniquesToCompare);
        const showSl = this.props.dependencyManager.isTechnologyValid(
            this.props.optionManager.availableSelections.techniquesToCompare, "_SL", this.props.phaseState.techniquesToCompare);
        const showPul = this.props.dependencyManager.isTechnologyValid(
            this.props.optionManager.availableSelections.techniquesToCompare, "_PUL", this.props.phaseState.techniquesToCompare);
        
        const diaByTubeType = this.getExistingDiameterByTubeType(this.props.phaseState);
        
        return (
            <div id={ `phase-${this.props.phaseNr}` } className="phase-input">
                { this.props.phaseNr > 1 && <hr className="print-hide phase-delimiter" /> }
                <div className="input-section">
                    <h1 className="input-section-header">Etapp {this.props.phaseNr}
                        { this.props.totalPhaseCount > 1 ?
                            <small className="print-hide"><a   style={{cursor: "pointer", textDecoration: "none"}}
                                                               onClick={() => this.props.removePhase(this.props.phaseNr)}> - Ta bort</a></small>
                            : null}
                    </h1>
                    <div className="input-section-content">
                        <CalculationTechInput optionManager={this.props.optionManager}

                                              onChange={this.handleChange}
                                              onBlur={this.handleBlur}
                                              onUnmount={this.handleUnmount}

                                              phaseNr={this.props.phaseNr}

                                              techniquesToCompare={this.props.phaseState.techniquesToCompare}
                                              techniquesToCompareMeta={this.meta('techniquesToCompare')} />

                    </div>
                </div>
                { techChosen && <hr className="input-section-delimiter"/>}


                { techChosen && <CalculationBasicInfoInput optionManager={this.props.optionManager}
                                                           dependencyManager={this.props.dependencyManager}

                                                           onChange={this.handleChange}
                                                           onBlur={this.handleBlur}
                                                           onUnmount={this.handleUnmount}
                                                           addTube={this.addTube}
                                                           removeTube={this.removeTube}

                                                           phaseNr={this.props.phaseNr}

                                                           typeOfLandscapeBefore={this.props.phaseState.typeOfLandscapeBefore}
                                                           coatingBefore={this.props.phaseState.coatingBefore}
                                                           streetTypeBefore={this.props.phaseState.streetTypeBefore}
                                                           distanceReceiverFillingKm={this.props.phaseState.distanceReceiverFillingKm}
                                                           distanceReceiverCleanAsphaltKm={this.props.phaseState.distanceReceiverCleanAsphaltKm}
                                                           distanceReceiverPollutedAsphaltKm={this.props.phaseState.distanceReceiverPollutedAsphaltKm}

                                                           typeOfLandscapeBeforeMeta={this.meta('typeOfLandscapeBefore')}
                                                           coatingBeforeMeta={this.meta('coatingBefore')}
                                                           streetTypeBeforeMeta={this.meta('streetTypeBefore')}
                                                           distanceReceiverFillingKmMeta={this.meta('distanceReceiverFillingKm')}
                                                           distanceReceiverCleanAsphaltKmMeta={this.meta('distanceReceiverCleanAsphaltKm')}
                                                           distanceReceiverPollutedAsphaltKmMeta={this.meta('distanceReceiverPollutedAsphaltKm')}

                                                           techniquesToCompare={this.props.phaseState.techniquesToCompare}

                                                           numTubes={this.props.phaseState.numExistingTubes}
                                                           numTubesName={"numExistingTubes"}
                                                           tubeValuesNamePrefix={existingTubesNamePrefix}
                                                           tubeValues={this.getPrefixedNumericValuesFromState(existingTubesNamePrefix)}
                                                           tubeMetas={this.tubeMetas(existingTubesNamePrefix)} /> }
                { techChosen && <hr className="input-section-delimiter"/>}
                
                { techChosen && <CalculationBasicInfoAfterInput optionManager={this.props.optionManager}
                                                                dependencyManager={this.props.dependencyManager}

                                                                onChange={this.handleChange}
                                                                onBlur={this.handleBlur}
                                                                onUnmount={this.handleUnmount}

                                                                phaseNr={this.props.phaseNr}

                                                                typeOfLandscapeAfter={this.props.phaseState.typeOfLandscapeAfter}
                                                                coatingAfter={this.props.phaseState.coatingAfter}
                                                                asphaltTypeAfter={this.props.phaseState.asphaltTypeAfter}
                                                                streetTypeAfter={this.props.phaseState.streetTypeAfter}
                                                                
                                                                typeOfLandscapeAfterMeta={this.meta('typeOfLandscapeAfter')}
                                                                coatingAfterMeta={this.meta('coatingAfter')}
                                                                streetTypeAfterMeta={this.meta('streetTypeAfter')}
                                                                asphaltTypeAfterMeta={this.meta('asphaltTypeAfter')}
                /> }
                { techChosen && <hr className="input-section-delimiter"/>}

                { techChosen && <CalculationDistancesInput optionManager={this.props.optionManager}
                                                           dependencyManager={this.props.dependencyManager}

                                                           onChange={this.handleChange}
                                                           onBlur={this.handleBlur}
                                                           onUnmount={this.handleUnmount}

                                                           phaseNr={this.props.phaseNr}

                                                           coatingBefore={this.props.phaseState.coatingBefore}

                                                           distanceReceiverFillingKm={this.props.phaseState.distanceReceiverFillingKm}
                                                           distanceReceiverCleanAsphaltKm={this.props.phaseState.distanceReceiverCleanAsphaltKm}
                                                           distanceReceiverPollutedAsphaltKm={this.props.phaseState.distanceReceiverPollutedAsphaltKm}
                                                           distanceReceiverOldTubesKm={this.props.phaseState.distanceReceiverOldTubesKm}
                                                           distanceFillingKm={this.props.phaseState.distanceFillingKm}
                                                           distanceAsphaltKm={50} // Hard code for now, not available in excel yet

                                                           distanceReceiverFillingKmMeta={this.meta('distanceReceiverFillingKm')}
                                                           distanceReceiverCleanAsphaltKmMeta={this.meta('distanceReceiverCleanAsphaltKm')}
                                                           distanceReceiverPollutedAsphaltKmMeta={this.meta('distanceReceiverPollutedAsphaltKm')}
                                                           distanceReceiverOldTubesKmMeta={this.meta('distanceReceiverOldTubesKm')}
                                                           distanceFillingKmMeta={this.meta('distanceFillingKm')}
                                                           distanceAsphaltKmMeta={this.meta('distanceAsphaltKm')}
                /> }
                { techChosen && <hr className="input-section-delimiter"/>}
                                                                

                { techChosen && <CalculationTraditionalDiggingInput optionManager={this.props.optionManager}
                                                                    dependencyManager={this.props.dependencyManager}

                                                                    onChange={this.handleChange}
                                                                    onBlur={this.handleBlur}
                                                                    onUnmount={this.handleUnmount}
                                                                    addTube={this.addTube}
                                                                    removeTube={this.removeTube}

                                                                    phaseNr={this.props.phaseNr}

                                                                    techniquesToCompare={this.props.phaseState.techniquesToCompare}

                                                                    shaftDepthM={this.props.phaseState.shaftDepthM}
                                                                    slopeAngleN1={this.props.phaseState.slopeAngleN1}
                                                                    shaftCapacityM3PerHour={this.props.phaseState.shaftCapacityM3PerHour}
                                                                    shaftLengthM={this.props.phaseState.shaftLengthM}

                                                                    shaftDepthMMeta={this.meta('shaftDepthM')}
                                                                    slopeAngleN1Meta={this.meta('slopeAngleN1')}
                                                                    shaftCapacityM3PerHourMeta={this.meta('shaftCapacityM3PerHour')}
                                                                    shaftLengthMMeta={this.meta('shaftLengthM')}
                                                                    
                                                                    minShaftLength={this.props.phaseState.existingTube1TubeLength}

                                                                    numTubes={this.props.phaseState.numTraditionalTubes}
                                                                    numTubesName={"numTraditionalTubes"}
                                                                    tubeValuesNamePrefix={traditionalTubesNamePrefix}
                                                                    tubeValues={this.getPrefixedNumericValuesFromState(traditionalTubesNamePrefix)}
                                                                    tubeMetas={this.tubeMetas(traditionalTubesNamePrefix)} /> }
                { techChosen && <hr className="input-section-delimiter"/>}

                { techChosen && showIfl && <CalculationInsertionFlexibleLiningInput optionManager={this.props.optionManager}
                                                                                    dependencyManager={this.props.dependencyManager}

                                                                                    onChange={this.handleChange}
                                                                                    onBlur={this.handleBlur}
                                                                                    onUnmount={this.handleUnmount}
                                                                                    addTube={this.addTube}
                                                                                    removeTube={this.removeTube}

                                                                                    phaseNr={this.props.phaseNr}

                                                                                    pointShafts={this.props.phaseState.iflPointShafts}
                                                                                    metersLongerBottomLengthThanWidth={this.props.phaseState.iflMetersLongerBottomLengthThanWidth}
                                                                                    shaftDepthM={this.props.phaseState.iflShaftDepthM}
                                                                                    slopeAngleN1={this.props.phaseState.iflSlopeAngleN1}
                                                                                    shaftCapacityM3PerHour={this.props.phaseState.iflShaftCapacityM3PerHour}
                                                                                    shaftLengthM={this.props.phaseState.iflShaftLengthM}

                                                                                    pointShaftsMeta={this.meta('iflPointShafts')}
                                                                                    metersLongerBottomLengthThanWidthMeta={this.meta('iflMetersLongerBottomLengthThanWidth')}
                                                                                    shaftDepthMMeta={this.meta('iflShaftDepthM')}
                                                                                    slopeAngleN1Meta={this.meta('iflSlopeAngleN1')}
                                                                                    shaftCapacityM3PerHourMeta={this.meta('iflShaftCapacityM3PerHour')}
                                                                                    shaftLengthMMeta={this.meta('iflShaftLengthM')}
                                                                                    
                                                                                    diaByTubeType={diaByTubeType}

                                                                                    numTubes={this.props.phaseState.numIflTubes}
                                                                                    numTubesName={"numIflTubes"}
                                                                                    tubeValuesNamePrefix={iflTubesNamePrefix}
                                                                                    tubeValues={this.getPrefixedNumericValuesFromState(iflTubesNamePrefix)}
                                                                                    tubeMetas={this.tubeMetas(iflTubesNamePrefix)} /> }
                { techChosen && showIfl && <hr className="input-section-delimiter"/>}

                { techChosen && showTc && <CalculationTubeCrackingInput optionManager={this.props.optionManager}
                                                                        dependencyManager={this.props.dependencyManager}

                                                                        onChange={this.handleChange}
                                                                        onBlur={this.handleBlur}
                                                                        onUnmount={this.handleUnmount}
                                                                        addTube={this.addTube}
                                                                        removeTube={this.removeTube}

                                                                        phaseNr={this.props.phaseNr}

                                                                        pointShafts={this.props.phaseState.tcPointShafts}
                                                                        metersLongerBottomLengthThanWidth={this.props.phaseState.tcMetersLongerBottomLengthThanWidth}
                                                                        shaftDepthM={this.props.phaseState.tcShaftDepthM}
                                                                        slopeAngleN1={this.props.phaseState.tcSlopeAngleN1}
                                                                        shaftCapacityM3PerHour={this.props.phaseState.tcShaftCapacityM3PerHour}
                                                                        shaftLengthM={this.props.phaseState.tcShaftLengthM}

                                                                        pointShaftsMeta={this.meta('tcPointShafts')}
                                                                        metersLongerBottomLengthThanWidthMeta={this.meta('tcMetersLongerBottomLengthThanWidth')}
                                                                        shaftDepthMMeta={this.meta('tcShaftDepthM')}
                                                                        slopeAngleN1Meta={this.meta('tcSlopeAngleN1')}
                                                                        shaftCapacityM3PerHourMeta={this.meta('tcShaftCapacityM3PerHour')}
                                                                        shaftLengthMMeta={this.meta('tcShaftLengthM')}
                                                                        
                                                                        diaByTubeType={diaByTubeType}
                                                                        
                                                                        numTubes={this.props.phaseState.numTcTubes}
                                                                        numTubesName={"numTcTubes"}
                                                                        tubeValuesNamePrefix={tcTubesNamePrefix}
                                                                        tubeValues={this.getPrefixedNumericValuesFromState(tcTubesNamePrefix)}
                                                                        tubeMetas={this.tubeMetas(tcTubesNamePrefix)} /> }
                { techChosen && showTc && <hr className="input-section-delimiter"/>}

                { techChosen && showDd && <CalculationDirectedDrillingInput optionManager={this.props.optionManager}
                                                                        dependencyManager={this.props.dependencyManager}

                                                                        onChange={this.handleChange}
                                                                        onBlur={this.handleBlur}
                                                                        onUnmount={this.handleUnmount}
                                                                        addTube={this.addTube}
                                                                        removeTube={this.removeTube}

                                                                        phaseNr={this.props.phaseNr}

                                                                        pointShafts={this.props.phaseState.ddPointShafts}
                                                                        metersLongerBottomLengthThanWidth={this.props.phaseState.ddMetersLongerBottomLengthThanWidth}
                                                                        shaftDepthM={this.props.phaseState.ddShaftDepthM}
                                                                        slopeAngleN1={this.props.phaseState.ddSlopeAngleN1}
                                                                        shaftCapacityM3PerHour={this.props.phaseState.ddShaftCapacityM3PerHour}
                                                                        shaftLengthM={this.props.phaseState.ddShaftLengthM}

                                                                        pointShaftsMeta={this.meta('ddPointShafts')}
                                                                        metersLongerBottomLengthThanWidthMeta={this.meta('ddMetersLongerBottomLengthThanWidth')}
                                                                        shaftDepthMMeta={this.meta('ddShaftDepthM')}
                                                                        slopeAngleN1Meta={this.meta('ddSlopeAngleN1')}
                                                                        shaftCapacityM3PerHourMeta={this.meta('ddShaftCapacityM3PerHour')}
                                                                        shaftLengthMMeta={this.meta('ddShaftLengthM')}
                                                                        
                                                                        diaByTubeType={diaByTubeType}
                                                                        
                                                                        numTubes={this.props.phaseState.numDdTubes}
                                                                        numTubesName={"numDdTubes"}
                                                                        tubeValuesNamePrefix={ddTubesNamePrefix}
                                                                        tubeValues={this.getPrefixedNumericValuesFromState(ddTubesNamePrefix)}
                                                                        tubeMetas={this.tubeMetas(ddTubesNamePrefix)} /> }
                { techChosen && showDd && <hr className="input-section-delimiter"/>}

                { techChosen && showSl && <CalculationSlipliningInput optionManager={this.props.optionManager}
                                                                                    dependencyManager={this.props.dependencyManager}

                                                                                    onChange={this.handleChange}
                                                                                    onBlur={this.handleBlur}
                                                                                    onUnmount={this.handleUnmount}
                                                                                    addTube={this.addTube}
                                                                                    removeTube={this.removeTube}

                                                                                    phaseNr={this.props.phaseNr}

                                                                                    pointShafts={this.props.phaseState.slPointShafts}
                                                                                    metersLongerBottomLengthThanWidth={this.props.phaseState.slMetersLongerBottomLengthThanWidth}
                                                                                    shaftDepthM={this.props.phaseState.slShaftDepthM}
                                                                                    slopeAngleN1={this.props.phaseState.slSlopeAngleN1}
                                                                                    shaftCapacityM3PerHour={this.props.phaseState.slShaftCapacityM3PerHour}
                                                                                    shaftLengthM={this.props.phaseState.slShaftLengthM}

                                                                                    pointShaftsMeta={this.meta('slPointShafts')}
                                                                                    metersLongerBottomLengthThanWidthMeta={this.meta('slMetersLongerBottomLengthThanWidth')}
                                                                                    shaftDepthMMeta={this.meta('slShaftDepthM')}
                                                                                    slopeAngleN1Meta={this.meta('slSlopeAngleN1')}
                                                                                    shaftCapacityM3PerHourMeta={this.meta('slShaftCapacityM3PerHour')}
                                                                                    shaftLengthMMeta={this.meta('slShaftLengthM')}

                                                                                    diaByTubeType={diaByTubeType}

                                                                                    numTubes={this.props.phaseState.numSlTubes}
                                                                                    numTubesName={"numSlTubes"}
                                                                                    tubeValuesNamePrefix={slTubesNamePrefix}
                                                                                    tubeValues={this.getPrefixedNumericValuesFromState(slTubesNamePrefix)}
                                                                                    tubeMetas={this.tubeMetas(slTubesNamePrefix)} /> }
                { techChosen && showSl && <hr className="input-section-delimiter"/>}
                                                                        
                { techChosen && showPul && <CalculationPuLinerInput optionManager={this.props.optionManager}
                                                                                    dependencyManager={this.props.dependencyManager}

                                                                                    onChange={this.handleChange}
                                                                                    onBlur={this.handleBlur}
                                                                                    onUnmount={this.handleUnmount}
                                                                                    addTube={this.addTube}
                                                                                    removeTube={this.removeTube}

                                                                                    phaseNr={this.props.phaseNr}

                                                                                    pointShafts={this.props.phaseState.pulPointShafts}
                                                                                    metersLongerBottomLengthThanWidth={this.props.phaseState.pulMetersLongerBottomLengthThanWidth}
                                                                                    shaftDepthM={this.props.phaseState.pulShaftDepthM}
                                                                                    slopeAngleN1={this.props.phaseState.pulSlopeAngleN1}
                                                                                    shaftCapacityM3PerHour={this.props.phaseState.pulShaftCapacityM3PerHour}
                                                                                    shaftLengthM={this.props.phaseState.pulShaftLengthM}

                                                                                    pointShaftsMeta={this.meta('pulPointShafts')}
                                                                                    metersLongerBottomLengthThanWidthMeta={this.meta('pulMetersLongerBottomLengthThanWidth')}
                                                                                    shaftDepthMMeta={this.meta('pulShaftDepthM')}
                                                                                    slopeAngleN1Meta={this.meta('pulSlopeAngleN1')}
                                                                                    shaftCapacityM3PerHourMeta={this.meta('pulShaftCapacityM3PerHour')}
                                                                                    shaftLengthMMeta={this.meta('pulShaftLengthM')}

                                                                                    diaByTubeType={diaByTubeType}

                                                                                    numTubes={this.props.phaseState.numPulTubes}
                                                                                    numTubesName={"numPulTubes"}
                                                                                    tubeValuesNamePrefix={pulTubesNamePrefix}
                                                                                    tubeValues={this.getPrefixedNumericValuesFromState(pulTubesNamePrefix)}
                                                                                    tubeMetas={this.tubeMetas(pulTubesNamePrefix)} /> }
                { techChosen && showPul && <hr className="input-section-delimiter"/>}
                                                                        

                
            </div>

        );
    };

    addTube = (numVariableName: string): void => {
        let newPartialState = this.props.dependencyManager.handleDependencies(numVariableName, this.props.phaseState[numVariableName] + 1, this.createDependencyContext(this.props.phaseState));
        this.props.changePhaseValues(this.props.phaseNr, newPartialState);
    };

    removeTube = (numVariableName: string): void => {
        let newPartialState = this.props.dependencyManager.handleDependencies(numVariableName, this.props.phaseState[numVariableName] - 1, this.createDependencyContext(this.props.phaseState));
        this.props.changePhaseValues(this.props.phaseNr, newPartialState);
    };

    getPrefixedNumericValuesFromState = (prefix: string) => {
        return Object.keys(this.props.phaseState)
            .filter(key => key.startsWith(prefix))
            .reduce((obj, key) => {
                obj[key] = Number(this.props.phaseState[key]);
                return obj;
            }, {});
    };

    meta(name: string) {
        if(typeof this.props.phaseState.error[name] === 'string') {
            return {
                valid: false,
                touched: this.props.phaseState.touched[name],
                errorMessage: this.props.phaseState.error[name]
            }
        }
        else
        {
            return {
                valid: true,
                touched: this.props.phaseState.touched[name] === true,
            }
        }
    }

    tubeMetas = (prefix: string) => {
        return Object.keys(this.props.phaseState)
            .filter(key => key.startsWith(prefix))
            .reduce((obj, key) => {
                obj[key] = this.meta(key);
                return obj;
            }, {});
    };
    
    getExistingDiameterByTubeType(state: PhaseState) {
        let retVal = [];
        for(let i = 1; i <= 3; i++) {
            if(state[`existingTube${i}TubeType`] !== -1) {
                let tubeType = state[`existingTube${i}TubeType`];
                let dia = state[`existingTube${i}TubeDiameter`];
                retVal[tubeType] = dia;
            }
        }
        return retVal;
    }
    
    createDependencyContext(state: PhaseState) : DependencyContext {
        return {
            diaByTubeType: this.getExistingDiameterByTubeType(state),

            existingTube1TubeType: state.existingTube1TubeType,
            existingTube2TubeType: state.existingTube2TubeType,
            existingTube3TubeType: state.existingTube3TubeType,

            tcTube1TubeType: state.tcTube1TubeType,
            tcTube2TubeType: state.tcTube2TubeType,
            tcTube3TubeType: state.tcTube3TubeType,

            ddTube1TubeType: state.ddTube1TubeType,
            ddTube2TubeType: state.ddTube2TubeType,
            ddTube3TubeType: state.ddTube3TubeType,
            
            slTube1TubeType: state.slTube1TubeType,
            
            numExistingTubes: state.numExistingTubes,
            numTraditionalTubes: state.numTraditionalTubes,
            techniquesToCompare: state.techniquesToCompare,
        }
    }

}

export default ReactAI.withTracking(CalculationPhaseInput); 



// WEBPACK FOOTER //
// ./src/components/CalculationPhaseInput.js